import { useDynamicTextResolver } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import type { DataGridText } from './types';

/**
 * Resolves DataGrid text configuration into ready-to-render strings and dynamic text resolvers.
 *
 * Provides default English strings for all labels and creates resolver functions for dynamic text
 * that depends on runtime values (e.g., page info with row counts, rows-per-page display).
 * Dynamic resolvers use `useDynamicTextResolver` to support both static strings and callback
 * functions from the `text` prop.
 */
export function useDataGridText<TData = unknown>(text: DataGridText<TData>) {
  const {
    title,
    searchPlaceholder,
    searchLabel = 'Search',
    clearSearch = 'Clear search',
    noRecordsFound = 'No records found',
    emptyCellLabel,
    rowsPerPage,
    pageInfo,
    previous = 'Previous',
    next = 'Next',
    sortAscending = 'Sort ascending',
    sortDescending = 'Sort descending',
    sortToggle = 'Toggle sort direction',
    pageSelectLabel = 'Select page size',
    goToPage,
    goToPrevPage = 'Go to previous page',
    goToNextPage = 'Go to next page',
    loading = 'Loading...',
    filtersLabel = 'Filter by',
    clearAllFilters = 'Clear',
    resultsFoundAnnouncement,
    showingItemsAnnouncement,
    expandableHeaderLabel = 'Expand rows',
    expandableRowTriggerCollapsedLabel,
    expandableRowTriggerExpandedLabel,
    selectAllRowsLabel = 'Select all rows on this page',
    selectRowLabel,
    selectExpandedSubItemLabel,
    selectedCountLabel,
    clearSelectionLabel = 'Clear selection',
    tableActionsLabel = 'Table actions',
    selectionBarLabel = 'Row actions',
    selectionOpenedLabel,
  } = text;

  const resolveRowsPerPage = useDynamicTextResolver(rowsPerPage, (size: number) => `${size}/page`);
  const resolvePageInfo = useDynamicTextResolver(
    pageInfo,
    (startRow: number, endRow: number, totalRows: number) =>
      `${startRow.toLocaleString()}\u2013${endRow.toLocaleString()} of ${totalRows.toLocaleString()}`,
  );
  const resolveResultsFoundAnnouncement = useDynamicTextResolver(
    resultsFoundAnnouncement,
    (totalRows: number) => `${totalRows.toLocaleString()} ${totalRows === 1 ? 'result' : 'results'} found`,
  );
  const resolveShowingItemsAnnouncement = useDynamicTextResolver(
    showingItemsAnnouncement,
    (startRow: number, endRow: number, totalRows: number) =>
      `Showing ${startRow.toLocaleString()}\u2013${endRow.toLocaleString()} of ${totalRows.toLocaleString()}`,
  );
  const resolveSelectRowLabel = useDynamicTextResolver(selectRowLabel, () => 'Select row');
  const resolveExpandCollapsedLabel = useDynamicTextResolver(expandableRowTriggerCollapsedLabel, () => 'Expand row');
  const resolveExpandExpandedLabel = useDynamicTextResolver(expandableRowTriggerExpandedLabel, () => 'Collapse row');
  const resolveSelectedCountLabel = useDynamicTextResolver(selectedCountLabel, (count: number) => `${count} selected`);
  const resolveSelectionOpenedLabel = useDynamicTextResolver(
    selectionOpenedLabel,
    (count: number) => `${count} selected. Row actions available.`,
  );

  return {
    title,
    searchPlaceholder,
    searchLabel,
    clearSearch,
    noRecordsFound,
    emptyCellLabel,
    previous,
    next,
    sortAscending,
    sortDescending,
    sortToggle,
    pageSelectLabel,
    goToPage,
    goToPrevPage,
    goToNextPage,
    loading,
    filtersLabel,
    clearAllFilters,
    expandableHeaderLabel,
    expandableRowTriggerCollapsedLabel,
    expandableRowTriggerExpandedLabel,
    selectAllRowsLabel,
    selectExpandedSubItemLabel,
    clearSelectionLabel,
    tableActionsLabel,
    selectionBarLabel,
    resolveRowsPerPage,
    resolvePageInfo,
    resolveResultsFoundAnnouncement,
    resolveShowingItemsAnnouncement,
    resolveSelectRowLabel,
    resolveExpandCollapsedLabel,
    resolveExpandExpandedLabel,
    resolveSelectedCountLabel,
    resolveSelectionOpenedLabel,
  };
}
